import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scale, Loader2 } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1656088304789-02d1ad6c9149?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBmYWNhZGV8ZW58MHx8fHwxNzc2Njg1ODkxfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) nav("/dashboard");
    else setErr(r.error);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left image panel */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${BG})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#7B1826]/70 via-[#0F172A]/60 to-[#0F172A]/90" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center rounded-sm">
              <Scale className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-2xl leading-none">Lexia</div>
              <div className="text-[10px] uppercase tracking-[0.2em] mt-1 text-white/70">
                Gestor Jurídico
              </div>
            </div>
          </div>
          <div>
            <h2 className="font-display text-4xl xl:text-5xl leading-tight max-w-md">
              Redacción jurídica asistida por IA para despachos serios.
            </h2>
            <p className="mt-5 text-white/80 text-sm max-w-md leading-relaxed">
              Combina plantillas, escritos judiciales y fundamentación para generar documentos
              de trabajo en minutos, no en horas.
            </p>
          </div>
          <div className="text-xs text-white/50 font-mono">v1.0 · self-hosted</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 lg:px-16 bg-[#FBFBF9]">
        <div className="w-full max-w-sm anim-fade-up">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="w-8 h-8 bg-[#7B1826] flex items-center justify-center rounded-sm">
              <Scale className="w-4 h-4 text-white" />
            </div>
            <span className="font-display text-xl">Lexia</span>
          </div>
          <h1 className="font-display text-4xl text-[#0F172A] mb-1">Bienvenido</h1>
          <p className="text-sm text-neutral-600 mb-8">Accede a tu espacio de trabajo jurídico.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-neutral-600">
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5 rounded-sm h-11 border-[#E5E7EB]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-neutral-600">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5 rounded-sm h-11 border-[#E5E7EB]"
              />
            </div>
            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-[#DC2626] border-l-2 border-[#DC2626] pl-3 py-1"
              >
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-sm bg-[#7B1826] hover:bg-[#5D121D] text-white font-medium tracking-wide transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Acceder"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
